import { MenuItem } from '@types'
import {
  MenuItemMod,
  MenuItemModGroup,
  CreateMenuItemModGroup,
  EditMenuItemModGroup,
  EditMenuItemMod,
  CreateMenuItemMod,
  ChefMenuItem,
} from '../../../types/groupOrder/menuItemMods'

interface MenuItemModsToChanges {
  createModGroups: CreateMenuItemModGroup[]
  editModGroups: EditMenuItemModGroup[]
}

const isNewId = (id: string): boolean => {
  return id.startsWith('new-')
}

const uneditableModGroupFields: (keyof MenuItemModGroup)[] = [
  'name',
  'verb',
  'isSingleOpt',
  'isRequired',
  'mods',
]
const uneditableModItemFields: (keyof MenuItemMod)[] = [
  'name',
  'clientPrice',
  'restaurantPrice',
  'retailPrice',
]

const pModItemDiffFields = (
  originalModItem: MenuItemMod,
  nextModItem: MenuItemMod,
  onlyUneditableFields: boolean,
): (keyof MenuItemMod)[] => {
  const diffFields = [] as (keyof MenuItemMod)[]

  Object.keys(originalModItem).forEach((key) => {
    const modItemKey = key as keyof MenuItemMod
    const checkKey = onlyUneditableFields
      ? uneditableModItemFields.includes(modItemKey)
      : true
    if (checkKey && originalModItem[modItemKey] !== nextModItem[modItemKey]) {
      diffFields.push(modItemKey)
    }
  })

  return diffFields
}

const pModGroupDiffFields = (
  originalModGroup: MenuItemModGroup,
  nextModGroup: MenuItemModGroup,
  onlyUneditableFields: boolean,
): (keyof MenuItemModGroup)[] => {
  const diffFields = [] as (keyof MenuItemModGroup)[]

  Object.keys(originalModGroup).forEach((key) => {
    const groupKey = key as keyof MenuItemModGroup

    if (groupKey === 'mods') {
      for (const originalModItem of originalModGroup.mods) {
        if (isNewId(originalModItem.id)) {
          diffFields.push(groupKey)
          break
        }
        const nextModItem = nextModGroup.mods.find(
          (mod) => mod.id === originalModItem.id,
        )
        if (!nextModItem) {
          // should always find nextModItem
          throw new Error(
            `should always find nextModItem for originalModItem ${originalModItem.name}`,
          )
        }
        const modItemDiffFields = pModItemDiffFields(
          originalModItem,
          nextModItem,
          onlyUneditableFields,
        )
        if (modItemDiffFields.length > 0) {
          diffFields.push(groupKey)
          break
        }
      }
    } else {
      const checkKey = onlyUneditableFields
        ? uneditableModGroupFields.includes(groupKey)
        : true
      if (checkKey && originalModGroup[groupKey] !== nextModGroup[groupKey]) {
        diffFields.push(groupKey)
      }
    }
  })

  return diffFields
}

const pModItemToCreateModItem = (
  nextModItem: MenuItemMod,
): CreateMenuItemMod => {
  return {
    name: nextModItem.name,
    clientPrice: nextModItem.clientPrice,
    restaurantPrice: nextModItem.restaurantPrice,
    retailPrice: nextModItem.retailPrice,
    position: nextModItem.position,
  }
}

const pModItemToEditModItem = (
  originalModItem: MenuItemMod,
  nextModItem: MenuItemMod,
): EditMenuItemMod => {
  const editModItem = {
    id: originalModItem.id,
  } as EditMenuItemMod
  if (nextModItem.position !== originalModItem.position) {
    editModItem.position = nextModItem.position
  }
  if (nextModItem.deletedAt) {
    editModItem.deleted = true
  }

  return editModItem
}

// calculates the delta between the original and next mod groups as a list of create and edit mod groups
// if a group's uneditable fields are changed - the group and its items will be deleted and recreated
// if a group's editable fields are changed - the group and its items will be edited
// if a group is new - it and its items will be created
// if a group is deleted - the group will be deleted - its items are not deleted but will be unnaccessible
// if a group item's uneditable fields are changed - the item will be deleted and recreated
// if a group item's editable fields are changed - the item will be edited
// if a group item is new - the item will be created
// if a group item is deleted - the item will be deleted
export const pMenuItemModsToChanges = (
  originalModGroups: MenuItemModGroup[],
  nextModGroups: MenuItemModGroup[],
): MenuItemModsToChanges => {
  const createModGroups = [] as CreateMenuItemModGroup[]
  const editModGroups = [] as EditMenuItemModGroup[]

  for (const nextModGroup of nextModGroups) {
    const isNewCreatedMod = isNewId(nextModGroup.id)
    const originalModGroup = originalModGroups.find(
      (modGroup) => modGroup.id === nextModGroup.id,
    )
    if (!originalModGroup && !isNewCreatedMod) {
      // should always find nextModGroup
      throw new Error(
        `should always find originalModGroup for not newly created mod group ${nextModGroup.name}`,
      )
    }
    const isUneditableGroupFieldChanged =
      originalModGroup &&
      pModGroupDiffFields(originalModGroup, nextModGroup, true).length > 0

    // create item mod edits
    const createGroupItemMods = [] as CreateMenuItemMod[]
    const editGroupItemMods = [] as EditMenuItemMod[]
    for (const nextModItem of nextModGroup.mods) {
      const isNewCreatedModItem = isNewId(nextModItem.id)
      const originalModItem = originalModGroup?.mods.find(
        (mod) => mod.id === nextModItem.id,
      )
      // should always find originalModItem if not newly created
      if (!originalModItem && !isNewCreatedModItem) {
        throw new Error(
          `should always find originalModItem for not newly created mod item ${nextModItem.name}`,
        )
      }
      // if the original mod item was already deleted - skip it
      // these should already be filtered out in api response presenter
      if (originalModItem && originalModItem.deletedAt) {
        continue
      }
      const isUneditableModItemFieldChanged =
        originalModItem &&
        pModItemDiffFields(originalModItem, nextModItem, true).length > 0

      // need to create new mod items if either the group or mod item has uneditable fields that are changed
      const isNewModItem =
        isNewCreatedModItem ||
        isUneditableModItemFieldChanged ||
        isUneditableGroupFieldChanged
      // dont include deleted items in new mod group
      if (!nextModItem.deletedAt && isNewModItem) {
        createGroupItemMods.push(pModItemToCreateModItem(nextModItem))
      } else if (originalModItem && !isNewModItem) {
        const editModItem = pModItemToEditModItem(originalModItem, nextModItem)
        if (Object.keys(editModItem).length > 1) {
          editGroupItemMods.push(editModItem)
        }
      }
    }

    // create group edits
    if (isNewCreatedMod || isUneditableGroupFieldChanged) {
      const createModGroup = {
        name: nextModGroup.name,
        verb: nextModGroup.verb,
        isSingleOpt: nextModGroup.isSingleOpt,
        position: nextModGroup.position,
      } as CreateMenuItemModGroup
      if (editGroupItemMods.length > 0 && isNewCreatedMod) {
        throw new Error(
          'should not have have option edits on newly created mod groups',
        )
      }
      if (createGroupItemMods.length > 0) {
        createModGroup.mods = createGroupItemMods
      }
      createModGroups.push(createModGroup)
      if (isUneditableGroupFieldChanged) {
        // delete current mod if an uneditable field is changed
        editModGroups.push({
          id: nextModGroup.id,
          deleted: true,
        } as EditMenuItemModGroup)
      }
    } else if (originalModGroup) {
      const editModGroup = {
        id: nextModGroup.id,
      } as EditMenuItemModGroup
      if (nextModGroup.position !== originalModGroup.position) {
        editModGroup.position = nextModGroup.position
      }
      if (nextModGroup.deletedAt) {
        editModGroup.deleted = true
      }

      if (createGroupItemMods.length > 0) {
        editModGroup.createdMods = createGroupItemMods
      }
      if (editGroupItemMods.length > 0) {
        editModGroup.editedMods = editGroupItemMods
      }
      if (Object.keys(editModGroup).length > 1) {
        editModGroups.push(editModGroup)
      }
    }
  }

  return { createModGroups, editModGroups }
}

export const pResponseChefMenuItemWithActiveMods = (
  chefMenuItem: ChefMenuItem,
): ChefMenuItem => {
  return {
    ...chefMenuItem,
    availableModGroups: chefMenuItem.availableModGroups
      .filter((modGroup) => !modGroup.deletedAt)
      .map((modGroup) => ({
        ...modGroup,
        mods: modGroup.mods
          .filter((mod) => !mod.deletedAt)
          .sort((a, b) => a.position - b.position),
      }))
      .sort((a, b) => a.position - b.position),
  }
}

export const pToAvailableModGroups = (modItem?: ChefMenuItem) => {
  if (!modItem || !modItem.modsActive) {
    return []
  }

  const validGroups = modItem.availableModGroups.filter((g) => !g.deletedAt)

  return validGroups.map((group) => {
    return {
      ...group,
      availableMods: group.mods.filter((modItem) => !modItem.deletedAt),
    }
  })
}

export const pMergeItemWithMods = (
  menuItem: MenuItem,
  modItem: ChefMenuItem,
): MenuItem => {
  return {
    ...menuItem,
    availableModGroups: pToAvailableModGroups(modItem),
  }
}
